import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Truck, Save, RefreshCw, AlertTriangle, X, CheckCircle } from 'lucide-react';
import MainService from '../../services/MainService';
import { mapVehicleDetailToForm, isApiSaveSuccess } from '../../utils/vehicleApiUtils';

const AddEditVehicle = () => {
  const navigate = useNavigate();
  const { vehicleId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [vehicle, setVehicle] = useState({
    Id: 0,
    Number: '',
    Model: '',
    Make: '',
    Year: '',
    Color: '',
    RegistrationNumber: '',
    ChassisNumber: '',
    EngineNumber: '',
    IsActive: true
  });

  useEffect(() => {
    if (vehicleId && parseInt(vehicleId) > 0) {
      loadVehicle(parseInt(vehicleId));
    }
  }, [vehicleId]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadVehicle = async (id) => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.VehicleById(id);
      const mapped = mapVehicleDetailToForm(response);
      if (mapped) {
        setVehicle(mapped);
      } else {
        setError('Vehicle not found');
      }
    } catch (err) {
      console.error('Error loading vehicle:', err);
      setError('Failed to load vehicle data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setVehicle(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!vehicle.Number || vehicle.Number.trim() === '') {
      setError('Enter Vehicle Number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const yearStr = String(vehicle.Year || '').trim();
      const yearVal =
        yearStr === '' ? null : Number.isFinite(Number(yearStr)) ? Number(yearStr) : yearStr;
      const payload = {
        Id: Number(vehicle.Id) || 0,
        Number: vehicle.Number.trim(),
        Model: vehicle.Model || '',
        Make: vehicle.Make || '',
        Year: yearVal,
        Color: vehicle.Color || '',
        RegistrationNumber: vehicle.RegistrationNumber || '',
        ChassisNumber: vehicle.ChassisNumber || '',
        EngineNumber: vehicle.EngineNumber || '',
        IsActive: !!vehicle.IsActive,
      };
      const response = await MainService.SaveVehicle(payload);
      if (!isApiSaveSuccess(response)) {
        setError('Failed to save vehicle');
        return;
      }
      setSuccess('Vehicle saved successfully');
      setTimeout(() => {
        navigate('/home/vehicle-management/vehicle-list');
      }, 800);
    } catch (err) {
      console.error('Error saving vehicle:', err);
      setError('Failed to save vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setVehicle({
      Id: 0,
      Number: '',
      Model: '',
      Make: '',
      Year: '',
      Color: '',
      RegistrationNumber: '',
      ChassisNumber: '',
      EngineNumber: '',
      IsActive: true
    });
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <Truck className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">
              {vehicleId && parseInt(vehicleId) > 0 ? 'Edit Vehicle' : 'Add Vehicle'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {loading && (
              <div className="w-6 h-6 border-2 border-[#46955A] border-t-transparent rounded-full animate-spin"></div>
            )}
            <button
              onClick={handleReset}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-[#46955A] text-[#46955A] rounded-lg hover:bg-[#f0f9f4] hover:border-[#3d7d4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              <Save className="w-4 h-4" />
              <span>Save</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 ml-8">
          {vehicleId && parseInt(vehicleId) > 0 
            ? 'Update vehicle information and details' 
            : 'Create new vehicle and manage their information'}
        </p>
      </div>

      {/* Form Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Vehicle Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={vehicle.Number}
              onChange={(e) => handleInputChange('Number', e.target.value)}
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Model</label>
            <input
              type="text"
              value={vehicle.Model}
              onChange={(e) => handleInputChange('Model', e.target.value)}
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Make</label>
            <input
              type="text"
              value={vehicle.Make}
              onChange={(e) => handleInputChange('Make', e.target.value)}
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Year</label>
            <input
              type="text"
              value={vehicle.Year}
              onChange={(e) => handleInputChange('Year', e.target.value)}
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Color</label>
            <input
              type="text"
              value={vehicle.Color}
              onChange={(e) => handleInputChange('Color', e.target.value)}
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Registration Number</label>
            <input
              type="text"
              value={vehicle.RegistrationNumber}
              onChange={(e) => handleInputChange('RegistrationNumber', e.target.value)}
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Chassis Number</label>
            <input
              type="text"
              value={vehicle.ChassisNumber}
              onChange={(e) => handleInputChange('ChassisNumber', e.target.value)}
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Engine Number</label>
            <input
              type="text"
              value={vehicle.EngineNumber}
              onChange={(e) => handleInputChange('EngineNumber', e.target.value)}
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select
              value={vehicle.IsActive ? 'Active' : 'Inactive'}
              onChange={(e) => handleInputChange('IsActive', e.target.value === 'Active')}
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditVehicle;

